import { useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { track } from '@/lib/analytics';
import { LpFooter, LpShell } from '@/shared/components/LpShell';
import { useTenant } from '@/modules/tenant/presentation/hooks/use-tenant';
import { useAuth } from '@/modules/auth/presentation/hooks/use-auth';
import { firstNameOf } from '@/modules/auth/domain/entities/auth.entity';
import {
  AuthModal,
  useAuthModal,
} from '@/modules/auth/presentation/components/AuthModal';
import { HeaderAccount } from '@/modules/auth/presentation/components/HeaderAccount';
import { DiagnosticoTab } from '../components/DiagnosticoTab';
import { EmptyState, TabSkeleton } from '../components/EmptyState';
import { EnsaioTab, type EnsaioSubTab } from '../components/EnsaioTab';
import { GabaritoTab } from '../components/GabaritoTab';
import { ProvaOficialTab } from '../components/ProvaOficialTab';
import { RankingTab } from '../components/RankingTab';
import { PrevisoesAristoSection } from '@/modules/simulado/presentation/components/EnsaioMaterials';
import { useMySubmission } from '@/modules/submissions/presentation/hooks/use-submissions';
import { Tabs, tabId } from '../components/Tabs';
import {
  PAINEL_TABS,
  resolveTabs,
  type PainelTab,
} from '../../domain/entities/tabs.entity';
import { useTabsConfig } from '../hooks/use-tabs-config';
import {
  DiagnosticoIcon,
  EnsaioIcon,
  GabaritoIcon,
  ProvaOficialIcon,
  RankingIcon,
} from '../components/tab-icons';

/** Rótulo e ícone de cada aba, na ordem da tela (`PAINEL_TABS`). */
const TABS: { id: PainelTab; label: string; icon: ReactNode }[] = [
  { id: 'ensaio', label: 'Ensaio', icon: <EnsaioIcon /> },
  { id: 'gabarito', label: 'Gabarito', icon: <GabaritoIcon /> },
  { id: 'ranking', label: 'Ranking', icon: <RankingIcon /> },
  { id: 'prova-oficial', label: 'Prova Oficial', icon: <ProvaOficialIcon /> },
  { id: 'diagnostico', label: 'Diagnóstico', icon: <DiagnosticoIcon /> },
];

function isPainelTab(value: string | null): value is PainelTab {
  return PAINEL_TABS.some((tab) => tab === value);
}

/**
 * O painel de quem tem conta (`/minha-conta`), com as cinco abas da edição.
 *
 * **A aba aberta mora na URL** (`?aba=gabarito`, e `&sub=` para a sub-aba do
 * Ensaio): é assim que o fim do ensaio leva direto ao diagnóstico e o fim do
 * gabarito à correção, e que recarregar a página não volta à primeira aba.
 *
 * **Quais abas aparecem vem do Remote Config** (`EXAMS_CAPTURE_TABS`, por
 * prova e ano — ver `tabs.entity.ts`): desligada some, "Em breve" aparece
 * desabilitada, e a `default` abre quando a URL não diz qual (ou diz uma que
 * não abre). Sem a configuração, só o Ensaio.
 *
 * Sem sessão, a página não redireciona: ela própria oferece o login, e quem
 * entra fica onde estava. Com sessão de uma conta sem perfil nesta plataforma
 * (veio de outra plataforma da Primum), as abas esperam o cadastro curto — o
 * back responderia 403 a todas elas.
 */
export function PainelPage() {
  const { exam, theme, edition } = useTenant();
  const { user, isRestoring } = useAuth();
  const [params, setParams] = useSearchParams();
  const { open, modalProps } = useAuthModal('login', () => {});

  const tabsConfig = useTabsConfig();
  const resolved =
    tabsConfig.status === 'ready' ? resolveTabs(tabsConfig.tabs) : null;
  const openable =
    resolved?.visible.filter((tab) => !resolved.preview.includes(tab)) ?? [];

  // A aba da URL vale se abre; senão, a inicial da configuração. `null` com a
  // configuração carregando, ou sem nenhuma aba que abra.
  const aba = params.get('aba');
  const active: PainelTab | null =
    isPainelTab(aba) && openable.includes(aba)
      ? aba
      : (resolved?.initial ?? null);

  // **Sem Previsões onde a pessoa faz o ensaio**: na aba Ensaio, só depois do
  // envio (antes dele, a aba é o próprio ensaio). Enquanto a leitura do envio
  // não volta, fica escondida, para não aparecer e sumir em seguida.
  const myEnsaio = useMySubmission('ensaio');
  const showPrevisoes =
    active !== null &&
    (active !== 'ensaio' || (myEnsaio.isSuccess && myEnsaio.data !== null));
  const sub: EnsaioSubTab =
    params.get('sub') === 'diagnostico' ? 'diagnostico' : 'questoes';

  // Uma "página vista" por aba: o painel é uma rota só, e sem isto o GA4 veria
  // uma visita ao painel inteiro, sem saber qual aba a pessoa abriu.
  const userId = user?.id;
  useEffect(() => {
    if (!userId || !active) return;
    track({
      event: 'painel_aba_vista',
      aba: active,
      ...(active === 'ensaio' ? { sub_aba: sub } : {}),
    });
  }, [userId, active, sub]);

  const goTo = (next: PainelTab, nextSub?: EnsaioSubTab) => {
    const search = new URLSearchParams({ aba: next });
    if (nextSub) search.set('sub', nextSub);
    setParams(search, { replace: true });
  };

  return (
    <LpShell theme={theme} headerRight={<HeaderAccount />}>
      <title>{`Minha conta | ${exam.shortName} ${edition.year}`}</title>

      <section className="fold">
        <div className="wrap">
          {isRestoring && <TabSkeleton label="Carregando sua conta" />}

          {!isRestoring && !user && (
            <EmptyState
              title="Entre na sua conta"
              note="Seu ensaio, seu gabarito, o ranking e os diagnósticos ficam na sua conta."
            >
              <button
                type="button"
                className="cta cta-inline"
                onClick={() => open({ kind: 'sign-in' })}
              >
                Entrar
              </button>
            </EmptyState>
          )}

          {user && !user.hasProfile && (
            <EmptyState
              title={`Olá, ${firstNameOf(user)}`}
              note={`Sua conta da Primum já existe. Confirme seus dados para acessar o ensaio, o gabarito e o ranking da ${exam.shortName}.`}
            >
              <button
                type="button"
                className="cta cta-inline"
                onClick={() => open({ kind: 'complete-profile' })}
              >
                Completar cadastro
              </button>
            </EmptyState>
          )}

          {user?.hasProfile && (
            <>
              <h1 className="page-title">Olá, {firstNameOf(user)}</h1>
              <p className="page-lead">
                {exam.shortName} {edition.year}: seu ensaio, seu gabarito e como
                você foi.
              </p>

              <div className="page-body stack-lg">
                {/* O carro-chefe, acima das abas. Só aparece se a prova tem o
                    link, e não enquanto a pessoa faz o ensaio. */}
                {showPrevisoes && <PrevisoesAristoSection />}

                {!resolved && <TabSkeleton label="Carregando as seções" />}

                {resolved && !active && (
                  <EmptyState
                    title="Em breve"
                    note={`As seções da ${exam.shortName} ${edition.year} abrem aqui em breve.`}
                  />
                )}

                {resolved && active && (
                  <>
                    {/* Com uma aba só, a barra não tem o que escolher. */}
                    {resolved.visible.length > 1 && (
                      <Tabs
                        idPrefix="painel"
                        label="Seções da sua conta"
                        options={TABS.filter((tab) =>
                          resolved.visible.includes(tab.id),
                        ).map((tab) => ({
                          ...tab,
                          isPreview: resolved.preview.includes(tab.id),
                        }))}
                        active={active}
                        onChange={(next) => goTo(next)}
                      />
                    )}

                    <div
                      role="tabpanel"
                      aria-labelledby={
                        resolved.visible.length > 1
                          ? tabId('painel', active)
                          : undefined
                      }
                      aria-label={
                        resolved.visible.length > 1
                          ? undefined
                          : TABS.find((tab) => tab.id === active)?.label
                      }
                    >
                      {active === 'ensaio' && (
                        <EnsaioTab
                          sub={sub}
                          onSubChange={(next) => goTo('ensaio', next)}
                        />
                      )}
                      {active === 'gabarito' && <GabaritoTab />}
                      {active === 'ranking' && <RankingTab />}
                      {active === 'prova-oficial' && <ProvaOficialTab />}
                      {active === 'diagnostico' && <DiagnosticoTab />}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <LpFooter />
      <AuthModal {...modalProps} />
    </LpShell>
  );
}
